import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, ChevronDownIcon, EyeIcon, FileEditIcon, SendIcon, RefreshCwIcon, MailIcon, CheckCircleIcon, XCircleIcon, XIcon, FileTextIcon, DownloadIcon, PrinterIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { StatusBadge, ExamStatus } from '../components/ui/StatusBadge';
import { TabGroup } from '../components/ui/TabGroup';
import { ModalDialog } from '../components/ui/ModalDialog';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useRole } from '../hooks/useRole';
// --- Types ---
interface Exam {
  id: string;
  patient: string;
  email: string;
  telefono: string;
  codiceFiscale: string;
  type: string;
  category: 'RM' | 'TAC' | 'ECO' | 'RX' | 'MAMMO';
  doctor: string;
  machine: string;
  date: string;
  time: string;
  status: ExamStatus;
}
interface Template {
  id: string;
  category: string;
  name: string;
  description: string;
}
// --- Constants & Mock Data ---
const TODAY = '2026-03-11';
const mockTemplates: Template[] = [{
  id: 't1',
  category: 'RM',
  name: 'Referto RM Standard',
  description: 'Modello base per risonanza magnetica con sezioni standard.'
}, {
  id: 't2',
  category: 'RM',
  name: 'Referto RM con Contrasto',
  description: 'Modello per risonanza magnetica con mezzo di contrasto.'
}, {
  id: 't3',
  category: 'TAC',
  name: 'Referto TAC Standard',
  description: 'Modello base per tomografia assiale computerizzata.'
}, {
  id: 't4',
  category: 'ECO',
  name: 'Referto Ecografia Addome',
  description: 'Modello specifico per ecografia addominale completa.'
}, {
  id: 't5',
  category: 'RX',
  name: 'Referto RX Standard',
  description: 'Modello base per esami radiografici tradizionali.'
}, {
  id: 't6',
  category: 'MAMMO',
  name: 'Referto Mammografia',
  description: 'Modello per screening mammografico bilaterale.'
}];
const initialExams: Exam[] = [{
  id: 'EXM-5001',
  patient: 'Marco Rossi',
  email: 'marco.rossi@email.it',
  telefono: '+39 333 1234567',
  codiceFiscale: 'RSSMRC80A01H501Z',
  type: 'Risonanza magnetica encefalo',
  category: 'RM',
  doctor: 'Dr. S. Verdi',
  machine: 'Siemens Magnetom',
  date: '2026-03-11',
  time: '08:00',
  status: 'In corso'
}, {
  id: 'EXM-5002',
  patient: 'Giulia Bianchi',
  email: 'giulia.bianchi@email.it',
  telefono: '+39 334 2345678',
  codiceFiscale: 'BNCGLI85B42F205X',
  type: 'TAC torace',
  category: 'TAC',
  doctor: 'Dr. A. Neri',
  machine: 'GE Revolution',
  date: '2026-03-11',
  time: '09:30',
  status: 'In attesa'
}, {
  id: 'EXM-5003',
  patient: 'Luca Romano',
  email: 'luca.romano@email.it',
  telefono: '+39 335 3456789',
  codiceFiscale: 'RMNLCU75C15D612Y',
  type: 'Radiografia colonna vertebrale',
  category: 'RX',
  doctor: 'Dr. M. Gialli',
  machine: 'Carestream DRX',
  date: '2026-03-11',
  time: '10:15',
  status: 'In attesa'
}, {
  id: 'EXM-5004',
  patient: 'Elena Colombo',
  email: 'elena.colombo@email.it',
  telefono: '+39 336 4567890',
  codiceFiscale: 'CLMLNE90D50F205W',
  type: 'Ecografia addome',
  category: 'ECO',
  doctor: 'Dr. S. Verdi',
  machine: 'Philips Affiniti',
  date: '2026-03-11',
  time: '11:00',
  status: 'Da refertare'
}, {
  id: 'EXM-5005',
  patient: 'Alessandro Ricci',
  email: 'alessandro.ricci@email.it',
  telefono: '+39 337 5678901',
  codiceFiscale: 'RCCLSS82E12H501V',
  type: 'Risonanza magnetica ginocchio',
  category: 'RM',
  doctor: 'Dr. A. Neri',
  machine: 'Siemens Magnetom',
  date: '2026-03-10',
  time: '14:30',
  status: 'Da refertare'
}, {
  id: 'EXM-5006',
  patient: 'Anna Ferrari',
  email: 'anna.ferrari@email.it',
  telefono: '+39 338 6789012',
  codiceFiscale: 'FRRNNA88F45F205U',
  type: 'TAC addome',
  category: 'TAC',
  doctor: 'Dr. A. Neri',
  machine: 'GE Revolution',
  date: '2026-03-10',
  time: '15:45',
  status: 'Refertato'
}, {
  id: 'EXM-5007',
  patient: 'Giuseppe Russo',
  email: 'giuseppe.russo@email.it',
  telefono: '+39 339 7890123',
  codiceFiscale: 'RSSGPP70G18H501T',
  type: 'Ecografia pelvi',
  category: 'ECO',
  doctor: 'Dr. M. Gialli',
  machine: 'Philips Affiniti',
  date: '2026-03-10',
  time: '16:30',
  status: 'Consegnato'
}, {
  id: 'EXM-5008',
  patient: 'Maria Conti',
  email: 'maria.conti@email.it',
  telefono: '+39 340 8901234',
  codiceFiscale: 'CNTMRA79H52F205S',
  type: 'Radiografia torace',
  category: 'RX',
  doctor: 'Dr. S. Verdi',
  machine: 'Carestream DRX',
  date: '2026-03-09',
  time: '09:00',
  status: 'Consegnato'
}, {
  id: 'EXM-5009',
  patient: 'Luigi Gallo',
  email: 'luigi.gallo@email.it',
  telefono: '+39 341 9012345',
  codiceFiscale: 'GLLLGU85I14H501R',
  type: 'Mammografia bilaterale',
  category: 'MAMMO',
  doctor: 'Dr. S. Verdi',
  machine: 'Hologic Selenia',
  date: '2026-03-09',
  time: '10:30',
  status: 'Refertato'
}, {
  id: 'EXM-5010',
  patient: 'Francesca Moretti',
  email: 'f.moretti@email.it',
  telefono: '+39 342 0123456',
  codiceFiscale: 'MRTFNC92L60F205Q',
  type: 'TAC pelvi',
  category: 'TAC',
  doctor: 'Dr. M. Gialli',
  machine: 'GE Revolution',
  date: '2026-03-09',
  time: '11:45',
  status: 'Da refertare'
}, {
  id: 'EXM-5011',
  patient: 'Roberto Colombo',
  email: 'r.colombo@email.it',
  telefono: '+39 343 1234567',
  codiceFiscale: 'CLMRRT78M22H501P',
  type: 'Risonanza magnetica colonna',
  category: 'RM',
  doctor: 'Dr. A. Neri',
  machine: 'Siemens Magnetom',
  date: '2026-03-08',
  time: '08:30',
  status: 'Consegnato'
}, {
  id: 'EXM-5012',
  patient: 'Chiara Rizzo',
  email: 'c.rizzo@email.it',
  telefono: '+39 344 2345678',
  codiceFiscale: 'RZZCHR86N48F205O',
  type: 'Ecografia addome',
  category: 'ECO',
  doctor: 'Dr. S. Verdi',
  machine: 'Philips Affiniti',
  date: '2026-03-08',
  time: '10:00',
  status: 'Refertato'
}, {
  id: 'EXM-5013',
  patient: 'Davide Fontana',
  email: 'd.fontana@email.it',
  telefono: '+39 345 3456789',
  codiceFiscale: 'FNTDVD81P10H501N',
  type: 'Radiografia colonna vertebrale',
  category: 'RX',
  doctor: 'Dr. M. Gialli',
  machine: 'Carestream DRX',
  date: '2026-03-08',
  time: '11:15',
  status: 'Consegnato'
}, {
  id: 'EXM-5014',
  patient: 'Sara Lombardi',
  email: 's.lombardi@email.it',
  telefono: '+39 346 4567890',
  codiceFiscale: 'LMBSRA90Q55F205M',
  type: 'TAC torace',
  category: 'TAC',
  doctor: 'Dr. A. Neri',
  machine: 'GE Revolution',
  date: '2026-03-07',
  time: '14:00',
  status: 'Consegnato'
}, {
  id: 'EXM-5015',
  patient: 'Andrea Barbieri',
  email: 'a.barbieri@email.it',
  telefono: '+39 347 5678901',
  codiceFiscale: 'BRBNDR75R25H501L',
  type: 'Risonanza magnetica encefalo',
  category: 'RM',
  doctor: 'Dr. S. Verdi',
  machine: 'Siemens Magnetom',
  date: '2026-03-07',
  time: '15:30',
  status: 'Refertato'
}, {
  id: 'EXM-5016',
  patient: 'Martina Costa',
  email: 'm.costa@email.it',
  telefono: '+39 348 6789012',
  codiceFiscale: 'CSTMTN88S60F205K',
  type: 'Ecografia pelvi',
  category: 'ECO',
  doctor: 'Dr. M. Gialli',
  machine: 'Philips Affiniti',
  date: '2026-03-07',
  time: '16:45',
  status: 'Da refertare'
}, {
  id: 'EXM-5017',
  patient: 'Simone Greco',
  email: 's.greco@email.it',
  telefono: '+39 349 7890123',
  codiceFiscale: 'GRCSMN82T15H501J',
  type: 'Radiografia torace',
  category: 'RX',
  doctor: 'Dr. A. Neri',
  machine: 'Carestream DRX',
  date: '2026-03-06',
  time: '09:15',
  status: 'Consegnato'
}, {
  id: 'EXM-5018',
  patient: 'Valentina Santoro',
  email: 'v.santoro@email.it',
  telefono: '+39 350 8901234',
  codiceFiscale: 'SNTVNT91U52F205I',
  type: 'Risonanza magnetica ginocchio',
  category: 'RM',
  doctor: 'Dr. S. Verdi',
  machine: 'Siemens Magnetom',
  date: '2026-03-06',
  time: '10:30',
  status: 'Refertato'
}, {
  id: 'EXM-5019',
  patient: 'Paolo De Luca',
  email: 'p.deluca@email.it',
  telefono: '+39 351 9012345',
  codiceFiscale: 'DLCPLA77V20H501H',
  type: 'TAC addome',
  category: 'TAC',
  doctor: 'Dr. M. Gialli',
  machine: 'GE Revolution',
  date: '2026-03-06',
  time: '11:45',
  status: 'Consegnato'
}, {
  id: 'EXM-5020',
  patient: 'Silvia Martini',
  email: 's.martini@email.it',
  telefono: '+39 352 0123456',
  codiceFiscale: 'MRTSLV85Z48F205G',
  type: 'Mammografia bilaterale',
  category: 'MAMMO',
  doctor: 'Dr. A. Neri',
  machine: 'Hologic Selenia',
  date: '2026-03-05',
  time: '08:30',
  status: 'Consegnato'
}, {
  id: 'EXM-5021',
  patient: 'Giovanni Esposito',
  email: 'g.esposito@email.it',
  telefono: '+39 353 1234567',
  codiceFiscale: 'SPSGNN72A10H501F',
  type: 'Ecografia addome',
  category: 'ECO',
  doctor: 'Dr. S. Verdi',
  machine: 'Philips Affiniti',
  date: '2026-03-05',
  time: '09:45',
  status: 'Refertato'
}, {
  id: 'EXM-5022',
  patient: 'Laura Bruno',
  email: 'l.bruno@email.it',
  telefono: '+39 354 2345678',
  codiceFiscale: 'BRNLRA89B55F205E',
  type: 'Radiografia colonna vertebrale',
  category: 'RX',
  doctor: 'Dr. M. Gialli',
  machine: 'Carestream DRX',
  date: '2026-03-05',
  time: '11:00',
  status: 'Consegnato'
}, {
  id: 'EXM-5023',
  point: 'EXM-5023',
  patient: 'Matteo Ferrari',
  email: 'm.ferrari@email.it',
  telefono: '+39 355 3456789',
  codiceFiscale: 'FRRMTT84C18H501D',
  type: 'Risonanza magnetica encefalo',
  category: 'RM',
  doctor: 'Dr. A. Neri',
  machine: 'Siemens Magnetom',
  date: '2026-03-05',
  time: '14:15',
  status: 'Da refertare',
  id: 'EXM-5023'
}, {
  id: 'EXM-5024',
  patient: 'Federica Russo',
  email: 'f.russo@email.it',
  telefono: '+39 356 4567890',
  codiceFiscale: 'RSSFDR93D62F205C',
  type: 'TAC torace',
  category: 'TAC',
  doctor: 'Dr. S. Verdi',
  machine: 'GE Revolution',
  date: '2026-03-05',
  time: '15:30',
  status: 'Refertato'
}, {
  id: 'EXM-5025',
  patient: 'Antonio Bianchi',
  email: 'a.bianchi@email.it',
  telefono: '+39 357 5678901',
  codiceFiscale: 'BNCNTN76E22H501B',
  type: 'Ecografia pelvi',
  category: 'ECO',
  doctor: 'Dr. M. Gialli',
  machine: 'Philips Affiniti',
  date: '2026-03-04',
  time: '09:00',
  status: 'Consegnato'
}, {
  id: 'EXM-5026',
  patient: 'Alessia Romano',
  email: 'a.romano@email.it',
  telefono: '+39 358 6789012',
  codiceFiscale: 'RMNLSS87F45F205A',
  type: 'Radiografia torace',
  category: 'RX',
  doctor: 'Dr. A. Neri',
  machine: 'Carestream DRX',
  date: '2026-03-04',
  time: '10:15',
  status: 'Consegnato'
}, {
  id: 'EXM-5027',
  patient: 'Stefano Colombo',
  email: 's.colombo@email.it',
  telefono: '+39 359 7890123',
  codiceFiscale: 'CLMSFN80G12H501Z',
  type: 'Risonanza magnetica colonna',
  category: 'RM',
  doctor: 'Dr. S. Verdi',
  machine: 'Siemens Magnetom',
  date: '2026-03-04',
  time: '11:30',
  status: 'Refertato'
}, {
  id: 'EXM-5028',
  patient: 'Elisa Ricci',
  email: 'e.ricci@email.it',
  telefono: '+39 360 8901234',
  codiceFiscale: 'RCCLSE91H50F205Y',
  type: 'TAC pelvi',
  category: 'TAC',
  doctor: 'Dr. M. Gialli',
  machine: 'GE Revolution',
  date: '2026-03-04',
  time: '14:45',
  status: 'Da refertare'
}, {
  id: 'EXM-5029',
  patient: 'Marco Marino',
  email: 'm.marino@email.it',
  telefono: '+39 361 9012345',
  codiceFiscale: 'MRNMRC74I18H501X',
  type: 'Ecografia addome',
  category: 'ECO',
  doctor: 'Dr. A. Neri',
  machine: 'Philips Affiniti',
  date: '2026-03-03',
  time: '08:30',
  status: 'Consegnato'
}, {
  id: 'EXM-5030',
  patient: 'Giulia Conti',
  email: 'g.conti@email.it',
  telefono: '+39 362 0123456',
  codiceFiscale: 'CNTGLL89L42F205W',
  type: 'Mammografia bilaterale',
  category: 'MAMMO',
  doctor: 'Dr. S. Verdi',
  machine: 'Hologic Selenia',
  date: '2026-03-03',
  time: '09:45',
  status: 'Refertato'
}];
// --- Helper Components ---
function TruncatedCell({
  text
}: {
  text: string;
}) {
  const [show, setShow] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const isMobileView = useIsMobile();
  const handleMouseEnter = () => {
    if (isMobileView) return;
    if (textRef.current) {
      if (textRef.current.scrollWidth > textRef.current.clientWidth) {
        setShow(true);
      }
    }
  };
  return <div className="relative min-w-0" onMouseEnter={handleMouseEnter} onMouseLeave={() => setShow(false)} data-id="element-855">
      <span ref={textRef} className="block truncate" data-id="element-856">
        {text}
      </span>
      {show && !isMobileView && <div className="fixed px-2.5 py-1.5 bg-text-primary text-white text-xs rounded-md whitespace-nowrap pointer-events-none shadow-lg" style={{
      zIndex: 9999,
      transform: 'translateY(-100%) translateY(-6px)'
    }} ref={el => {
      if (el) {
        const parent = el.parentElement?.getBoundingClientRect();
        if (parent) {
          el.style.top = `${parent.top}px`;
          el.style.left = `${parent.left}px`;
        }
      }
    }} data-id="element-857">
          {text}
        </div>}
    </div>;
}
export function DiagnosticExams() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const {
    role
  } = useRole();
  const CURRENT_USER = role === 'medico' ? 'Dr. S. Verdi' : 'Superadmin';
  // --- State ---
  const [exams, setExams] = useState<Exam[]>(initialExams);
  const [activeTab, setActiveTab] = useState('all');
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('Tutti');
  const [machineFilter, setMachineFilter] = useState('Tutti');
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  // Bulk Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Modals
  type ModalType = 'detail' | 'template' | 'editor' | 'delivery' | 'resend' | null;
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  // Report Form State
  const [reportForm, setReportForm] = useState({
    intro: '',
    rilevazioni: '',
    clinica: '',
    conclusioni: ''
  });
  // Toast
  const [toastMessage, setToastMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const dashboardExams = useMemo(() => {
    if (role === 'medico') {
      return exams.filter(e => e.doctor === CURRENT_USER);
    }
    return exams;
  }, [role, CURRENT_USER, exams]);
  // --- Derived Data ---
  const uniqueDoctors = useMemo(() => Array.from(new Set(dashboardExams.map(e => e.doctor))).sort(), [dashboardExams]);
  const uniqueMachines = useMemo(() => Array.from(new Set(dashboardExams.map(e => e.machine))).sort(), [dashboardExams]);
  const filteredExams = useMemo(() => {
    let result = [...dashboardExams];
    // Tab filter
    if (activeTab !== 'all') {
      const statusMap: Record<string, ExamStatus> = {
        'in-attesa': 'In attesa',
        'in-corso': 'In corso',
        'da-refertare': 'Da refertare',
        refertati: 'Refertato',
        consegnati: 'Consegnato'
      };
      result = result.filter(e => e.status === statusMap[activeTab]);
    }
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(e => e.id.toLowerCase().includes(q) || e.patient.toLowerCase().includes(q) || e.type.toLowerCase().includes(q) || e.doctor.toLowerCase().includes(q));
    }
    // Date filter
    if (dateFilter) {
      result = result.filter(e => e.date === dateFilter);
    }
    // Doctor filter
    if (doctorFilter !== 'Tutti' && role !== 'medico') {
      result = result.filter(e => e.doctor === doctorFilter);
    }
    // Machine filter
    if (machineFilter !== 'Tutti') {
      result = result.filter(e => e.machine === machineFilter);
    }
    return result;
  }, [dashboardExams, activeTab, searchQuery, dateFilter, doctorFilter, machineFilter, role]);
  const totalPages = Math.ceil(filteredExams.length / itemsPerPage);
  const paginatedExams = filteredExams.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Reset pagination and selection on filter change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [activeTab, searchQuery, dateFilter, doctorFilter, machineFilter]);
  // Tab Counts
  const tabCounts = useMemo(() => {
    return {
      all: dashboardExams.length,
      'in-attesa': dashboardExams.filter(e => e.status === 'In attesa').length,
      'in-corso': dashboardExams.filter(e => e.status === 'In corso').length,
      'da-refertare': dashboardExams.filter(e => e.status === 'Da refertare').length,
      refertati: dashboardExams.filter(e => e.status === 'Refertato').length,
      consegnati: dashboardExams.filter(e => e.status === 'Consegnato').length
    };
  }, [dashboardExams]);
  const tabs = [{
    id: 'all',
    label: 'Tutti gli esami',
    count: tabCounts.all
  }, {
    id: 'in-attesa',
    label: 'In attesa',
    count: tabCounts['in-attesa']
  }, {
    id: 'in-corso',
    label: 'In corso',
    count: tabCounts['in-corso']
  }, {
    id: 'da-refertare',
    label: 'Da refertare',
    count: tabCounts['da-refertare']
  }, {
    id: 'refertati',
    label: 'Refertati',
    count: tabCounts['refertati']
  }, {
    id: 'consegnati',
    label: 'Consegnati',
    count: tabCounts['consegnati']
  }];
  // --- Handlers ---
  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({
      type,
      text
    });
    setTimeout(() => setToastMessage(null), 4000);
  };
  const handleOpenDetail = (exam: Exam) => {
    setSelectedExam(exam);
    setActiveModal('detail');
  };
  const handleStartReporting = (exam: Exam) => {
    setSelectedExam(exam);
    setSelectedTemplateId(null);
    setActiveModal('template');
  };
  const handleTemplateNext = () => {
    if (!selectedTemplateId) return;
    setReportForm({
      intro: '',
      rilevazioni: '',
      clinica: '',
      conclusioni: ''
    });
    setActiveModal('editor');
  };
  const handleEditorSave = () => {
    setActiveModal('delivery');
  };
  const handleDeliveryChoice = (sendNow: boolean) => {
    if (!selectedExam) return;
    const newStatus: ExamStatus = sendNow ? 'Consegnato' : 'Refertato';
    setExams(prev => prev.map(e => e.id === selectedExam.id ? {
      ...e,
      status: newStatus
    } : e));
    setActiveModal(null);
    setSelectedExam(null);
    if (sendNow) {
      showToast('success', 'Referto salvato e inviato correttamente al paziente.');
    } else {
      showToast('success', "Referto salvato correttamente. Pronto per l'invio.");
    }
  };
  const handleOpenSend = (exam: Exam) => {
    setSelectedExam(exam);
    setActiveModal('delivery');
  };
  const handleOpenResend = (exam: Exam) => {
    setSelectedExam(exam);
    setActiveModal('resend');
  };
  const confirmResend = () => {
    if (!selectedExam) return;
    setActiveModal(null);
    setSelectedExam(null);
    showToast('success', 'Referto reinviato correttamente al paziente.');
  };
  // --- Bulk Selection Handlers ---
  const toggleSelection = (id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedIds(newSelection);
  };
  const toggleAllFiltered = () => {
    const allFilteredIds = filteredExams.map(e => e.id);
    const allSelected = allFilteredIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allFilteredIds));
    }
  };
  const handleBulkExport = () => {
    showToast('success', `Esportazione di ${selectedIds.size} referti in PDF avviata.`);
    setSelectedIds(new Set());
  };
  const handleBulkDownload = () => {
    showToast('success', `Download di ${selectedIds.size} referti avviato.`);
    setSelectedIds(new Set());
  };
  const isAllFilteredSelected = filteredExams.length > 0 && filteredExams.every(e => selectedIds.has(e.id));
  // --- Render Helpers ---
  const renderActions = (item: Exam) => {
    return <div className="flex items-center gap-1.5" data-id="element-858">
        {isMobile ? <button onClick={() => handleOpenDetail(item)} className="p-2 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors inline-flex items-center gap-1.5 text-xs font-medium min-h-[36px]" aria-label="Visualizza dettagli" data-id="element-859">
            <EyeIcon size={15} data-id="element-860" /> Dettaglio
          </button> : <div className="relative group" data-id="element-861">
            <button onClick={() => handleOpenDetail(item)} className="p-1.5 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors" aria-label="Visualizza dettagli" data-id="element-862">
              <EyeIcon size={16} data-id="element-863" />
            </button>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50" data-id="element-864">
              Visualizza dettagli
            </div>
          </div>}

        {item.status === 'Da refertare' && <button onClick={() => navigate(`/exams/report/${item.id}`)} className="medical-btn-primary py-1 px-2.5 text-xs whitespace-nowrap inline-flex items-center gap-1.5 min-h-[36px] md:min-h-0 md:py-1" data-id="element-865">
            <FileEditIcon size={13} data-id="element-866" /> Referta Esame
          </button>}

        {item.status === 'Refertato' && <button onClick={() => navigate(`/exams/report/${item.id}`)} className="medical-btn-secondary py-1 px-2.5 text-xs whitespace-nowrap inline-flex items-center gap-1.5 border-medical-green text-medical-green hover:bg-medical-green-50 min-h-[36px] md:min-h-0 md:py-1" data-id="element-867">
            <SendIcon size={13} data-id="element-868" /> Invia referto
          </button>}

        {item.status === 'Consegnato' && <button onClick={() => navigate(`/exams/report/${item.id}`)} className="medical-btn-secondary py-1 px-2.5 text-xs whitespace-nowrap inline-flex items-center gap-1.5 min-h-[36px] md:min-h-0 md:py-1" data-id="element-869">
            <RefreshCwIcon size={13} data-id="element-870" /> Reinvia referto
          </button>}
      </div>;
  };
  return <div className="space-y-6 w-full min-w-0 pb-12" data-id="element-871">
      {/* Header */}
      <div data-id="element-872">
        <h1 className="text-2xl md:text-page-title mb-1 md:mb-2 text-text-primary font-bold" data-id="element-873">
          Esami e Referti
        </h1>
        <p className="text-sm md:text-body text-text-secondary" data-id="element-874">
          Gestione operativa degli esami diagnostici e della refertazione.
        </p>
      </div>

      {/* Tabs */}
      <TabGroup tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className="mb-4 md:mb-6" data-id="element-875" />

      {/* Bulk Action Toolbar */}
      <AnimatePresence data-id="element-876">
        {selectedIds.size > 0 && <motion.div initial={{
        opacity: 0,
        height: 0,
        marginBottom: 0
      }} animate={{
        opacity: 1,
        height: 'auto',
        marginBottom: 16
      }} exit={{
        opacity: 0,
        height: 0,
        marginBottom: 0
      }} className="overflow-hidden" data-id="element-877">
            <div className="bg-medical-green-50 border border-medical-green-200 rounded-medical-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-878">
              <span className="text-sm font-medium text-medical-green" data-id="element-879">
                {selectedIds.size} referti selezionati
              </span>
              <div className="flex items-center gap-2" data-id="element-880">
                <button onClick={handleBulkExport} className="medical-btn-secondary py-1.5 px-3 text-xs bg-white border-medical-green-200 text-medical-green hover:bg-white hover:border-medical-green" data-id="element-881">
                  <PrinterIcon size={14} className="mr-1.5" data-id="element-882" /> Esporta
                  selezionati in PDF
                </button>
                <button onClick={handleBulkDownload} className="medical-btn-secondary py-1.5 px-3 text-xs bg-white border-medical-green-200 text-medical-green hover:bg-white hover:border-medical-green" data-id="element-883">
                  <DownloadIcon size={14} className="mr-1.5" data-id="element-884" /> Scarica referti
                  PDF
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center bg-white p-4 rounded-medical-lg shadow-sm border border-clinical-gray-100" data-id="element-885">
        <div className="relative w-full md:w-64" data-id="element-886">
          <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-887" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca per ID, paziente, esame..." className="medical-input pl-9 text-sm h-9 w-full" data-id="element-888" />
        </div>

        <input type="date" value={dateFilter} max={TODAY} onChange={e => setDateFilter(e.target.value)} className="medical-input text-sm h-9 w-full md:w-auto" data-id="element-889" />

        {role !== 'medico' && <div className="relative w-full md:w-auto" data-id="element-890">
            <select value={doctorFilter} onChange={e => setDoctorFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full md:w-auto" data-id="element-891">
              <option value="Tutti" data-id="element-892">Medico: Tutti</option>
              {uniqueDoctors.map(d => <option key={d} value={d} data-id="element-893">
                  {d}
                </option>)}
            </select>
            <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-894" />
          </div>}

        <div className="relative w-full md:w-auto" data-id="element-895">
          <select value={machineFilter} onChange={e => setMachineFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full md:w-auto" data-id="element-896">
            <option value="Tutti" data-id="element-897">Macchinario: Tutti</option>
            {uniqueMachines.map(m => <option key={m} value={m} data-id="element-898">
                {m}
              </option>)}
          </select>
          <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-899" />
        </div>
      </div>

      {/* Table / Cards */}
      {!isMobile ? <div className="w-full max-w-full bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 overflow-x-auto overflow-y-visible" data-id="element-900">
          <table className="w-full text-left border-collapse" style={{
        tableLayout: 'fixed',
        minWidth: '950px'
      }} data-id="element-901">
            <colgroup data-id="element-902">
              {role !== 'medico' ? <>
                  <col style={{
              width: '4%'
            }} data-id="element-903" />
                  <col style={{
              width: '11%'
            }} data-id="element-904" />
                  <col style={{
              width: '15%'
            }} data-id="element-905" />
                  <col style={{
              width: '17%'
            }} data-id="element-906" />
                  <col style={{
              width: '13%'
            }} data-id="element-907" />
                  <col style={{
              width: '13%'
            }} data-id="element-908" />
                  <col style={{
              width: '12%'
            }} data-id="element-909" />
                  <col style={{
              width: '15%'
            }} data-id="element-910" />
                </> : <>
                  <col style={{
              width: '4%'
            }} data-id="element-911" />
                  <col style={{
              width: '13%'
            }} data-id="element-912" />
                  <col style={{
              width: '18%'
            }} data-id="element-913" />
                  <col style={{
              width: '20%'
            }} data-id="element-914" />
                  <col style={{
              width: '15%'
            }} data-id="element-915" />
                  <col style={{
              width: '14%'
            }} data-id="element-916" />
                  <col style={{
              width: '16%'
            }} data-id="element-917" />
                </>}
            </colgroup>
            <thead data-id="element-918">
              <tr className="bg-clinical-gray border-b border-clinical-gray-200" data-id="element-919">
                <th className="px-4 py-3 text-center" data-id="element-920">
                  <input type="checkbox" checked={isAllFilteredSelected} onChange={toggleAllFiltered} className="w-4 h-4 rounded border-clinical-gray-300 text-medical-green focus:ring-medical-green cursor-pointer" data-id="element-921" />
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-922">
                  ID Esame
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-923">
                  Paziente
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-924">
                  Tipo Esame
                </th>
                {role !== 'medico' && <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-925">
                    Medico
                  </th>}
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-926">
                  Data
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-927">
                  Stato
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-928">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody data-id="element-929">
              {paginatedExams.length === 0 ? <tr data-id="element-930">
                  <td colSpan={8} className="px-4 py-8 text-center text-text-secondary text-body" data-id="element-931">
                    Nessun esame trovato con i filtri correnti.
                  </td>
                </tr> : paginatedExams.map(item => <tr key={item.id} className={`border-b border-clinical-gray-100 last:border-0 hover:bg-clinical-gray/30 transition-colors ${selectedIds.has(item.id) ? 'bg-medical-green-50/50' : ''}`} data-id="element-932">
                    <td className="px-4 py-3 text-center" data-id="element-933">
                      <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleSelection(item.id)} className="w-4 h-4 rounded border-clinical-gray-300 text-medical-green focus:ring-medical-green cursor-pointer" data-id="element-934" />
                    </td>
                    <td className="px-4 py-3 text-body text-text-primary overflow-hidden" data-id="element-935">
                      <TruncatedCell text={item.id} data-id="element-936" />
                    </td>
                    <td className="px-4 py-3 text-body text-text-primary overflow-hidden" data-id="element-937">
                      <TruncatedCell text={item.patient} data-id="element-938" />
                    </td>
                    <td className="px-4 py-3 text-body text-text-primary overflow-hidden" data-id="element-939">
                      <TruncatedCell text={item.type} data-id="element-940" />
                    </td>
                    {role !== 'medico' && <td className="px-4 py-3 text-body text-text-primary overflow-hidden" data-id="element-941">
                        <TruncatedCell text={item.doctor} data-id="element-942" />
                      </td>}
                    <td className="px-4 py-3 text-body text-text-secondary whitespace-nowrap" data-id="element-943">
                      {item.date} {item.time}
                    </td>
                    <td className="px-4 py-3" data-id="element-944">
                      <StatusBadge status={item.status} data-id="element-945" />
                    </td>
                    <td className="px-4 py-3" data-id="element-946">{renderActions(item)}</td>
                  </tr>)}
            </tbody>
          </table>
        </div> : <div className="space-y-3" data-id="element-947">
          {paginatedExams.length === 0 ? <div className="p-6 text-center text-text-secondary text-body bg-white rounded-medical-lg shadow-card border border-clinical-gray-100" data-id="element-948">
              Nessun esame trovato con i filtri correnti.
            </div> : paginatedExams.map(item => <div key={item.id} className={`bg-white rounded-medical-lg shadow-card border p-4 space-y-3 ${selectedIds.has(item.id) ? 'border-medical-green ring-1 ring-medical-green' : 'border-clinical-gray-100'}`} data-id="element-949">
                <div className="flex justify-between items-start" data-id="element-950">
                  <div className="flex items-start gap-3 min-w-0 mr-3" data-id="element-951">
                    <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleSelection(item.id)} className="w-4 h-4 mt-1 rounded border-clinical-gray-300 text-medical-green focus:ring-medical-green cursor-pointer shrink-0" data-id="element-952" />
                    <div className="min-w-0" data-id="element-953">
                      <span className="text-xs text-text-tertiary block mb-0.5" data-id="element-954">
                        {item.id}
                      </span>
                      <span className="text-sm font-medium text-text-primary truncate block" data-id="element-955">
                        {item.patient}
                      </span>
                    </div>
                  </div>
                  <StatusBadge status={item.status} data-id="element-956" />
                </div>
                <div className="grid grid-cols-2 gap-2 pl-7" data-id="element-957">
                  <div className="min-w-0" data-id="element-958">
                    <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-0.5" data-id="element-959">
                      Esame
                    </span>
                    <span className="text-sm text-text-primary truncate block" data-id="element-960">
                      {item.type}
                    </span>
                  </div>
                  <div className="min-w-0" data-id="element-961">
                    <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-0.5" data-id="element-962">
                      Data
                    </span>
                    <span className="text-sm text-text-primary truncate block" data-id="element-963">
                      {item.date}
                    </span>
                  </div>
                </div>
                <div className="pt-3 border-t border-clinical-gray-100 flex justify-end pl-7" data-id="element-964">
                  {renderActions(item)}
                </div>
              </div>)}
        </div>}

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-between px-1" data-id="element-965">
          <span className="text-sm text-text-secondary" data-id="element-966">
            {filteredExams.length} risultati — Pagina {currentPage} di{' '}
            {totalPages}
          </span>
          <div className="flex items-center gap-2" data-id="element-967">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-8 h-8 flex items-center justify-center rounded border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-968">
              <ChevronDownIcon size={16} className="rotate-90" data-id="element-969" />
            </button>
            <div className="flex items-center gap-1" data-id="element-970">
              {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-colors ${currentPage === page ? 'bg-medical-green text-white border border-medical-green' : 'border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50'}`} data-id="element-971">
                  {page}
                </button>)}
            </div>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-8 h-8 flex items-center justify-center rounded border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-972">
              <ChevronDownIcon size={16} className="-rotate-90" data-id="element-973" />
            </button>
          </div>
        </div>}

      {/* --- Modals --- */}

      {/* Detail Modal */}
      <ModalDialog isOpen={activeModal === 'detail'} onClose={() => setActiveModal(null)} title="Dettagli Esame" maxWidth="max-w-2xl" footer={<button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setActiveModal(null)} data-id="element-975">
            Chiudi
          </button>} data-id="element-974">
        {selectedExam && <div className="space-y-6" data-id="element-976">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-977">
              <div className="space-y-4" data-id="element-978">
                <div data-id="element-979">
                  <span className="text-xs text-text-tertiary block mb-1" data-id="element-980">
                    Paziente
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-981">
                    {selectedExam.patient}
                  </span>
                </div>
                <div data-id="element-982">
                  <span className="text-xs text-text-tertiary block mb-1" data-id="element-983">
                    Codice Fiscale
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-984">
                    {selectedExam.codiceFiscale}
                  </span>
                </div>
                <div data-id="element-985">
                  <span className="text-xs text-text-tertiary block mb-1" data-id="element-986">
                    Email Paziente
                  </span>
                  <div className="flex items-center gap-1.5 text-sm text-text-primary" data-id="element-987">
                    <MailIcon size={14} className="text-text-tertiary" data-id="element-988" />
                    {selectedExam.email}
                  </div>
                </div>
                <div data-id="element-989">
                  <span className="text-xs text-text-tertiary block mb-1" data-id="element-990">
                    Telefono
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-991">
                    {selectedExam.telefono}
                  </span>
                </div>
                <div data-id="element-992">
                  <span className="text-xs text-text-tertiary block mb-1" data-id="element-993">
                    Stato Corrente
                  </span>
                  <StatusBadge status={selectedExam.status} data-id="element-994" />
                </div>
              </div>
              <div className="space-y-4" data-id="element-995">
                <div data-id="element-996">
                  <span className="text-xs text-text-tertiary block mb-1" data-id="element-997">
                    Prestazione
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-998">
                    {selectedExam.type}
                  </span>
                </div>
                <div data-id="element-999">
                  <span className="text-xs text-text-tertiary block mb-1" data-id="element-1000">
                    Tipologia Esame
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1001">
                    {selectedExam.category}
                  </span>
                </div>
                <div data-id="element-1002">
                  <span className="text-xs text-text-tertiary block mb-1" data-id="element-1003">
                    Medico Refertatore
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1004">
                    {selectedExam.doctor}
                  </span>
                </div>
                <div data-id="element-1005">
                  <span className="text-xs text-text-tertiary block mb-1" data-id="element-1006">
                    Macchinario Utilizzato
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1007">
                    {selectedExam.machine}
                  </span>
                </div>
                <div data-id="element-1008">
                  <span className="text-xs text-text-tertiary block mb-1" data-id="element-1009">
                    Data e Ora
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1010">
                    {selectedExam.date} alle {selectedExam.time}
                  </span>
                </div>
              </div>
            </div>
          </div>}
      </ModalDialog>

      {/* Template Selection Modal */}
      <ModalDialog isOpen={activeModal === 'template'} onClose={() => setActiveModal(null)} title="Seleziona modello referto" maxWidth="max-w-2xl" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setActiveModal(null)} data-id="element-1012">
              Annulla
            </button>
            <button className="medical-btn-primary w-full sm:w-auto" disabled={!selectedTemplateId} onClick={handleTemplateNext} data-id="element-1013">
              Seleziona modello{' '}
              <ChevronDownIcon size={16} className="ml-1 -rotate-90" data-id="element-1014" />
            </button>
          </>} data-id="element-1011">
        {selectedExam && <div className="space-y-4" data-id="element-1015">
            <p className="text-sm text-text-secondary" data-id="element-1016">
              Modelli compatibili per l'esame:{' '}
              <span className="font-semibold text-text-primary" data-id="element-1017">
                {selectedExam.type}
              </span>
            </p>
            <div className="grid grid-cols-1 gap-3" data-id="element-1018">
              {mockTemplates.filter(t => t.category === selectedExam.category).map(template => <div key={template.id} onClick={() => setSelectedTemplateId(template.id)} className={`p-4 rounded-medical-lg border cursor-pointer transition-all ${selectedTemplateId === template.id ? 'border-medical-green bg-medical-green-50 ring-1 ring-medical-green' : 'border-clinical-gray-200 hover:border-medical-green/50 hover:bg-clinical-gray-50'}`} data-id="element-1019">
                    <div className="flex items-start gap-3" data-id="element-1020">
                      <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${selectedTemplateId === template.id ? 'border-medical-green' : 'border-clinical-gray-300'}`} data-id="element-1021">
                        {selectedTemplateId === template.id && <div className="w-2 h-2 rounded-full bg-medical-green" data-id="element-1022" />}
                      </div>
                      <div data-id="element-1023">
                        <h4 className="text-sm font-medium text-text-primary mb-1" data-id="element-1024">
                          {template.name}
                        </h4>
                        <p className="text-xs text-text-secondary" data-id="element-1025">
                          {template.description}
                        </p>
                      </div>
                    </div>
                  </div>)}
              {mockTemplates.filter(t => t.category === selectedExam.category).length === 0 && <div className="p-4 text-center text-sm text-text-secondary bg-clinical-gray-50 rounded-medical-lg" data-id="element-1026">
                  Nessun modello specifico trovato per questa categoria.
                </div>}
            </div>
          </div>}
      </ModalDialog>

      {/* Editor Modal */}
      <ModalDialog isOpen={activeModal === 'editor'} onClose={() => setActiveModal(null)} title="Compilazione Referto" maxWidth="max-w-4xl" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setActiveModal(null)} data-id="element-1028">
              Annulla
            </button>
            <button className={`medical-btn-primary w-full sm:w-auto ${!reportForm.intro && !reportForm.rilevazioni && !reportForm.clinica && !reportForm.conclusioni ? 'bg-clinical-gray-200 text-text-tertiary border-clinical-gray-200 cursor-not-allowed hover:bg-clinical-gray-200' : ''}`} disabled={!reportForm.intro && !reportForm.rilevazioni && !reportForm.clinica && !reportForm.conclusioni} onClick={handleEditorSave} data-id="element-1029">
              <FileTextIcon size={16} className="mr-1.5" data-id="element-1030" /> Salva referto
            </button>
          </>} data-id="element-1027">
        {selectedExam && selectedTemplateId && <div className="space-y-6" data-id="element-1031">
            {/* Comprehensive Header */}
            <div className="bg-clinical-gray-50 p-5 rounded-medical-lg border border-clinical-gray-200" data-id="element-1032">
              <h4 className="text-sm font-semibold text-text-primary mb-4 pb-2 border-b border-clinical-gray-200" data-id="element-1033">
                Informazioni Esame
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1034">
                <div data-id="element-1035">
                  <p className="text-xs text-text-tertiary uppercase tracking-wider mb-1" data-id="element-1036">
                    Paziente
                  </p>
                  <p className="text-sm font-medium text-text-primary" data-id="element-1037">
                    {selectedExam.patient}
                  </p>
                </div>
                <div data-id="element-1038">
                  <p className="text-xs text-text-tertiary uppercase tracking-wider mb-1" data-id="element-1039">
                    Codice Fiscale
                  </p>
                  <p className="text-sm font-medium text-text-primary" data-id="element-1040">
                    {selectedExam.codiceFiscale}
                  </p>
                </div>
                <div data-id="element-1041">
                  <p className="text-xs text-text-tertiary uppercase tracking-wider mb-1" data-id="element-1042">
                    Email
                  </p>
                  <p className="text-sm font-medium text-text-primary" data-id="element-1043">
                    {selectedExam.email}
                  </p>
                </div>
                <div data-id="element-1044">
                  <p className="text-xs text-text-tertiary uppercase tracking-wider mb-1" data-id="element-1045">
                    Telefono
                  </p>
                  <p className="text-sm font-medium text-text-primary" data-id="element-1046">
                    {selectedExam.telefono}
                  </p>
                </div>
                <div data-id="element-1047">
                  <p className="text-xs text-text-tertiary uppercase tracking-wider mb-1" data-id="element-1048">
                    Tipologia Esame
                  </p>
                  <p className="text-sm font-medium text-text-primary" data-id="element-1049">
                    {selectedExam.type}
                  </p>
                </div>
                <div data-id="element-1050">
                  <p className="text-xs text-text-tertiary uppercase tracking-wider mb-1" data-id="element-1051">
                    Data e Ora
                  </p>
                  <p className="text-sm font-medium text-text-primary" data-id="element-1052">
                    {selectedExam.date} {selectedExam.time}
                  </p>
                </div>
                <div data-id="element-1053">
                  <p className="text-xs text-text-tertiary uppercase tracking-wider mb-1" data-id="element-1054">
                    Macchinario
                  </p>
                  <p className="text-sm font-medium text-text-primary" data-id="element-1055">
                    {selectedExam.machine}
                  </p>
                </div>
                <div data-id="element-1056">
                  <p className="text-xs text-text-tertiary uppercase tracking-wider mb-1" data-id="element-1057">
                    Modello Referto
                  </p>
                  <p className="text-sm font-medium text-medical-green" data-id="element-1058">
                    {mockTemplates.find(t => t.id === selectedTemplateId)?.name}
                  </p>
                </div>
              </div>
            </div>

            {/* Editor Sections */}
            <div className="space-y-4" data-id="element-1059">
              <div data-id="element-1060">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1061">
                  Introduzione
                </label>
                <textarea className="medical-input w-full p-3 text-sm resize-y" rows={3} placeholder="Inserire indicazioni cliniche e tecnica di esame..." value={reportForm.intro} onChange={e => setReportForm(prev => ({
              ...prev,
              intro: e.target.value
            }))} data-id="element-1062" />
              </div>
              <div data-id="element-1063">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1064">
                  Rilevazioni
                </label>
                <textarea className="medical-input w-full p-3 text-sm resize-y" rows={5} placeholder="Inserire i reperti oggettivi dell'esame..." value={reportForm.rilevazioni} onChange={e => setReportForm(prev => ({
              ...prev,
              rilevazioni: e.target.value
            }))} data-id="element-1065" />
              </div>
              <div data-id="element-1066">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1067">
                  Valutazione clinica
                </label>
                <textarea className="medical-input w-full p-3 text-sm resize-y" rows={4} placeholder="Inserire la valutazione clinica dei reperti..." value={reportForm.clinica} onChange={e => setReportForm(prev => ({
              ...prev,
              clinica: e.target.value
            }))} data-id="element-1068" />
              </div>
              <div data-id="element-1069">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1070">
                  Conclusioni
                </label>
                <textarea className="medical-input w-full p-3 text-sm resize-y" rows={3} placeholder="Inserire le conclusioni diagnostiche..." value={reportForm.conclusioni} onChange={e => setReportForm(prev => ({
              ...prev,
              conclusioni: e.target.value
            }))} data-id="element-1071" />
              </div>
            </div>
          </div>}
      </ModalDialog>

      {/* Delivery Prompt Modal */}
      <ModalDialog isOpen={activeModal === 'delivery'} onClose={() => setActiveModal(null)} title="Vuoi inviare il referto via email al paziente?" maxWidth="max-w-lg" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => handleDeliveryChoice(false)} data-id="element-1073">
              Invia più tardi
            </button>
            <button className="medical-btn-primary w-full sm:w-auto" onClick={() => handleDeliveryChoice(true)} data-id="element-1074">
              <SendIcon size={16} className="mr-1.5" data-id="element-1075" /> Invia subito
            </button>
          </>} data-id="element-1072">
        {selectedExam && <div className="space-y-4" data-id="element-1076">
            <div className="bg-status-info-bg border border-status-info/20 rounded-medical-lg p-4 flex items-start gap-3" data-id="element-1077">
              <MailIcon className="text-status-info shrink-0 mt-0.5" size={20} data-id="element-1078" />
              <div data-id="element-1079">
                <p className="text-sm text-text-primary font-medium mb-1" data-id="element-1080">
                  Il referto verrà inviato via email.
                </p>
                <p className="text-sm text-text-secondary" data-id="element-1081">
                  L'email conterrà il referto in formato PDF in allegato, un
                  link sicuro per accedere all'area personale del paziente e la
                  possibilità di consultare lo storico dei referti precedenti.
                </p>
              </div>
            </div>
            <div className="bg-white border border-clinical-gray-200 rounded-medical-lg p-4 space-y-3" data-id="element-1082">
              <div className="flex justify-between items-center border-b border-clinical-gray-100 pb-3" data-id="element-1083">
                <span className="text-sm text-text-secondary" data-id="element-1084">Paziente</span>
                <span className="text-sm font-medium text-text-primary" data-id="element-1085">
                  {selectedExam.patient}
                </span>
              </div>
              <div className="flex justify-between items-center pt-1" data-id="element-1086">
                <span className="text-sm text-text-secondary" data-id="element-1087">
                  Email di destinazione
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-1088">
                  {selectedExam.email}
                </span>
              </div>
            </div>
          </div>}
      </ModalDialog>

      {/* Resend Modal */}
      <ModalDialog isOpen={activeModal === 'resend'} onClose={() => setActiveModal(null)} title="Reinvia Referto" maxWidth="max-w-lg" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setActiveModal(null)} data-id="element-1090">
              Annulla
            </button>
            <button className="medical-btn-primary w-full sm:w-auto" onClick={confirmResend} data-id="element-1091">
              <RefreshCwIcon size={16} className="mr-1.5" data-id="element-1092" /> Reinvia referto
            </button>
          </>} data-id="element-1089">
        {selectedExam && <div className="space-y-4" data-id="element-1093">
            <p className="text-sm text-text-secondary" data-id="element-1094">
              Sei sicuro di voler reinviare il referto per l'esame{' '}
              <span className="font-medium text-text-primary" data-id="element-1095">
                {selectedExam.type}
              </span>{' '}
              al paziente{' '}
              <span className="font-medium text-text-primary" data-id="element-1096">
                {selectedExam.patient}
              </span>
              ?
            </p>
            <div className="bg-clinical-gray-50 border border-clinical-gray-200 rounded-medical-lg p-4" data-id="element-1097">
              <div className="flex items-center gap-2 text-sm text-text-primary" data-id="element-1098">
                <MailIcon size={16} className="text-text-tertiary" data-id="element-1099" />
                L'email verrà inviata a:{' '}
                <span className="font-semibold" data-id="element-1100">{selectedExam.email}</span>
              </div>
            </div>
          </div>}
      </ModalDialog>

      {/* Toast Notification */}
      <AnimatePresence data-id="element-1101">
        {toastMessage && <motion.div initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} className={`fixed bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 sm:bottom-6 z-50 flex items-center gap-3 px-4 py-3 rounded-medical-lg shadow-modal border bg-white w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] sm:w-auto sm:max-w-md ${toastMessage.type === 'success' ? 'border-status-success/20' : 'border-status-error/20'}`} data-id="element-1102">
            {toastMessage.type === 'success' ? <CheckCircleIcon className="text-status-success shrink-0" size={20} data-id="element-1103" /> : <XCircleIcon className="text-status-error shrink-0" size={20} data-id="element-1104" />}
            <span className="text-sm font-medium text-text-primary" data-id="element-1105">
              {toastMessage.text}
            </span>
            <button onClick={() => setToastMessage(null)} className="ml-2 p-1 text-text-tertiary hover:text-text-primary rounded-full transition-colors" data-id="element-1106">
              <XIcon size={16} data-id="element-1107" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}